import { z } from 'zod';
import { Alert } from './alerts.js';
import { CameraStatus } from './enums.js';
import { VisionEvent } from './events.js';
/**
 * WebSocket contract.
 *
 * Loss tolerance differs per channel and that difference is deliberate:
 * occupancy is a throttled snapshot (dropping one is harmless — the next arrives
 * in a second), while events and alerts are durable in Postgres and the client
 * replays anything it missed over REST on reconnect. Nothing important depends
 * on the socket staying up.
 */
export const RT_CHANNELS = [
    'events',
    'alerts',
    'camera.health',
    'occupancy',
    'system.health',
];
export const RtChannel = z.enum(RT_CHANNELS);
export const OccupancySnapshot = z.object({
    totalOccupancy: z.number().int().nonnegative(),
    zones: z.array(z.object({
        zoneId: z.string().uuid(),
        zoneName: z.string(),
        kind: z.string(),
        cameraId: z.string().uuid(),
        occupancy: z.number().int().nonnegative(),
        capacity: z.number().int().nullable(),
    })),
    observedAt: z.string().datetime(),
});
export const CameraHealthUpdate = z.object({
    cameraId: z.string().uuid(),
    name: z.string(),
    status: CameraStatus,
    previousStatus: CameraStatus.nullable(),
    fps: z.number().nullable(),
    reason: z.string().nullable(),
    lastFrameAt: z.string().datetime().nullable(),
    changedAt: z.string().datetime(),
});
export const SystemHealthUpdate = z.object({
    service: z.string(),
    healthy: z.boolean(),
    detail: z.string().nullable(),
    observedAt: z.string().datetime(),
});
export const ServerMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ready'), ts: z.string(), channels: z.array(RtChannel) }),
    z.object({ type: z.literal('pong'), ts: z.string() }),
    z.object({ type: z.literal('event'), ts: z.string(), payload: VisionEvent }),
    z.object({ type: z.literal('alert'), ts: z.string(), payload: Alert }),
    z.object({ type: z.literal('alert.updated'), ts: z.string(), payload: Alert }),
    z.object({ type: z.literal('camera.health'), ts: z.string(), payload: CameraHealthUpdate }),
    z.object({ type: z.literal('occupancy'), ts: z.string(), payload: OccupancySnapshot }),
    z.object({ type: z.literal('system.health'), ts: z.string(), payload: SystemHealthUpdate }),
    z.object({ type: z.literal('error'), ts: z.string(), code: z.string(), message: z.string() }),
]);
export const ClientMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ping') }),
    z.object({ type: z.literal('subscribe'), channels: z.array(RtChannel).max(RT_CHANNELS.length) }),
]);
/** Redis pub/sub channel used to fan messages out across API replicas. */
export const RT_BROADCAST_CHANNEL = 'rt:broadcast';
/**
 * Envelope on the pub/sub channel. `visibleToZoneKinds` lets each API replica
 * apply role-based zone filtering locally without another database round trip —
 * a socket only ever receives what its role is allowed to see.
 */
export const BroadcastEnvelope = z.object({
    channel: RtChannel,
    message: ServerMessage,
    visibleToZoneKinds: z.array(z.string()).nullable(),
});
/** Occupancy is throttled server-side; the UI cannot use more than this. */
export const OCCUPANCY_BROADCAST_INTERVAL_MS = 1000;
export const WS_HEARTBEAT_INTERVAL_MS = 20_000;
export const WS_TICKET_TTL_SECONDS = 30;
//# sourceMappingURL=realtime.js.map